import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  Grid,
  Snackbar,
  Alert,
  Fade,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Code, Copy, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SetupFlow } from "../components/SetupFlow";
import { ChatWidget } from "../components/ChatWidget";
import { origin } from "../../resource/Constant";
import Header from "./Header";
import ChatbotDynamicTheme from "./ChatbotDynamicTheme";

const Index = () => {
  const [isSetupComplete, setIsSetupComplete] = useState(() => {
    return localStorage.getItem("is_vector_database_done") === "true";
  });
  const [showEmbedCode, setShowEmbedCode] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });
  const [openTheme, setOpenTheme] = useState(false);
  const navigate = useNavigate();

  const handleSetupComplete = () => {
    localStorage.setItem("is_vector_database_done", "true");
    setIsSetupComplete(true);
    navigate('/crawling/index');
  };
  
const embedCode = `<iframe 
  id="lovable-chatbot"
  src="${origin}/#/crawling/embed-chat/{#user-email}" 
  frameborder="0"
  allowtransparency="true"
  scrolling="no"
  style="position: fixed; bottom: 16px; right: 16px; width: 100px; height: 100px; border: none; background: transparent; z-index: 999999; overflow: hidden;"
></iframe>
<script>
  (function() {
    var iframe = document.getElementById('lovable-chatbot');
    if (!iframe) return;

    function resize(open, w, h) {
      if (open && w && h) {
        iframe.style.width = (w+30) + 'px';
        iframe.style.height = (h+40) + 'px';
      } else {
        iframe.style.width = '100px';
        iframe.style.height = '100px';
      }
      iframe.style.bottom = '16px';
      iframe.style.right = '16px';
    }

    window.addEventListener('message', function(e) {
      if (e && e.data && e.data.type) {
        console.log('[chatbot-embed] message', e.data);
      }
      if (!e.data || typeof e.data !== 'object') return;
      if (e.data.type === 'chatbot-resize') {
        resize(e.data.open, e.data.width, e.data.height);
      } else if (e.data.type === 'chatbot-ready') {
        // ensure starting size
        resize(false, 100, 100);
      }
    });
  })();
</script>`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(embedCode);
    setSnackbar({ open: true, message: "Embed code copied to clipboard", severity: "success" });
  };

  return (
    <>
    <Header/>
      {!isSetupComplete ? (
        <SetupFlow onComplete={handleSetupComplete} />
      ) : (
        <Box
          sx={{
            minHeight: "100vh",
            p: 4,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Animated background */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 160,
                left: -160,
                width: 320,
                height: 320,
                bgcolor: "primary.main",
                opacity: 0.2,
                borderRadius: "50%",
                filter: "blur(60px)",
                animation: "pulse 3s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 0.2 },
                  "50%": { opacity: 0.3 },
                },
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 240,
                right: -160,
                width: 384,
                height: 384,
                bgcolor: "success.main",
                opacity: 0.2,
                borderRadius: "50%",  
                filter: "blur(60px)",
                animation: "pulse 3s ease-in-out infinite",
                animationDelay: "1.5s",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 0.2 },
                  "50%": { opacity: 0.3 },
                },
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 80,
                left: "33%",
                width: 288,
                height: 288,
                bgcolor: "info.main",
                opacity: 0.2,
                borderRadius: "50%",
                filter: "blur(60px)",
                animation: "pulse 3s ease-in-out infinite",
                animationDelay: "3s",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 0.2 },
                  "50%": { opacity: 0.3 },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              maxWidth: 1280,
              mx: "auto",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box sx={{ textAlign: "center", mb: 6 , paddingTop : 4}}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  fontSize: { xs: "2.5rem", md: "3.75rem" },
                  background: "linear-gradient(135deg, #1976d2, #66bb6a)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Your AI Assistant is Live
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 672, mx: "auto" }}
              >
                Click the chat icon in the bottom right to test your assistant. It's trained on
                your website content and ready to assist visitors.
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ maxWidth: 960, mx: "auto", mb: 6 }}>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: "center",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    border: "2px solid",
                    borderColor: "primary.main",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: 8,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #1976d2, #66bb6a)",
                      mb: 2,
                      boxShadow: 4,
                    }}
                  >
                    <Typography sx={{ fontSize: "2rem" }}>🌐</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Website Crawled
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All pages indexed and ready
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: "center",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    border: "2px solid",
                    borderColor: "primary.main",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: 8,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #66bb6a, #81c784)",
                      mb: 2,
                      boxShadow: 4,
                    }}
                  >
                    <Typography sx={{ fontSize: "2rem" }}>📄</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Documents Added
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    PDFs processed and searchable
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: "center",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    border: "2px solid",
                    borderColor: "primary.main",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: 8,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #1976d2, #42a5f5, #66bb6a)",
                      mb: 2,
                      boxShadow: 4,
                    }}
                  >
                    <Typography sx={{ fontSize: "2rem" }}>🤖</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    AI Powered
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Smart responses
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Embed Code Section */}
            <Stack spacing={2} sx={{ maxWidth: 768, mx: "auto", mt: 6 }}>
              <Button
                onClick={() => navigate("/crawling/dashboard")}
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<Settings size={20} />}
                sx={{
                  py: 1.5,
                  fontSize: "1.125rem",
                  borderColor: "primary.main",
                  "&:hover": {
                    bgcolor: "action.hover",
                    transform: "scale(1.02)",
                    borderColor: "primary.dark",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Open Dashboard
              </Button>

              <Button
                onClick={() => setShowEmbedCode(!showEmbedCode)}
                variant="contained"
                fullWidth
                size="large"
                startIcon={<Code size={20} />}
                sx={{
                  py: 1.5,
                  fontSize: "1.125rem",
                  background: "linear-gradient(90deg, #1976d2, #42a5f5, #66bb6a)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #1565c0, #1976d2, #42a5f5)",
                    transform: "scale(1.02)",
                    boxShadow: 8,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Get Embed Code for Your Website
              </Button>

              {showEmbedCode && (
                <Fade in timeout={500}>
                  <Card
                    sx={{
                      mt: 2,
                      p: 3,
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      border: "2px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    <Stack spacing={2}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", background: "linear-gradient(135deg, #1976d2, #66bb6a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                          Embed Code
                        </Typography>
                        <Button
                          onClick={copyToClipboard}
                          variant="outlined"
                          size="small"
                          startIcon={<Copy size={16} />}
                          sx={{ borderColor: "primary.main" }}
                        >
                          Copy
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Copy this code and paste it anywhere in your website's HTML. The chatbot
                        appears as a floating icon at the bottom-right and won't cover your content:
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: "rgba(0,0,0,0.02)",
                          borderColor: "primary.main",
                          overflowX: "auto",
                        }}
                      >
                        <Typography
                          component="pre"
                          sx={{
                            fontSize: "0.875rem",
                            fontFamily: "monospace",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                            m: 0,
                          }}
                        >
                          {embedCode}
                        </Typography>
                      </Paper>
                      <Box sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Or use this direct link to view the assistant:
                        </Typography>
                        <Typography
                          component="a"
                          href={`${origin}/#/crawling/embed-chat/{#user_email}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: "primary.main",
                            fontWeight: 500,
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {origin}/#/crawling/embed-chat
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Fade>
              )}
            </Stack>
          </Box>
        </Box>
      )}

      {isSetupComplete && <ChatWidget />}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {isSetupComplete && (
        <>
          <Box
            sx={{
              position: "fixed",
              top: 104,
              left: 8,
              zIndex: 1300,
              pointerEvents: "none",
            }}
          >
            <Button
              variant="contained"
              startIcon={<Settings size={16} />}
              onClick={() => setOpenTheme(true)}
              sx={{
                pointerEvents: "auto",
                borderRadius: 3,
                boxShadow: 6,
                background: "linear-gradient(135deg, #1976d2, #42a5f5)",
              }}
            >
              Theme Customizer
            </Button>
          </Box>
          <Dialog
            open={openTheme}
            onClose={() => setOpenTheme(false)}
            maxWidth="sm"
            fullWidth={false}
            PaperProps={{
              sx: {
                width: 500,
                maxWidth: "90%",
                position: "absolute",
                left: 8,
                top: 80,
                m: 0,
                maxHeight: "calc(100vh - 120px)",
                overflow: "auto",
                borderRadius: 2,
                boxShadow: 12,
                paddingLeft : 3,
                paddingTop : 2
              },
            }}
          >
            <DialogTitle sx={{ fontWeight: 700, pb: 1.5 }}>Theme Customizer</DialogTitle>
            <DialogContent dividers sx={{ p: 0, pt: 0 }}>
              <ChatbotDynamicTheme />
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.5 }}>
              <Button variant="outlined" onClick={() => setOpenTheme(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default Index;
